import { Autocomplete, Box, Button, IconButton, Paper, TextField, Typography } from '@mui/material';
import { BillableMaterial, ConditionSet } from '../types';
import { JobsiteDTO } from '../../../api/generated';
import { Delete } from '@mui/icons-material';
import ConditionSetSummary from './ConditionSetSummary';
import { useTranslation } from 'react-i18next';
import MaterialConditionSet from './MaterialConditionSet';
import DeleteMaterialDialog from '../DeleteMaterialDialog';
import { useMemo, useState } from 'react';

interface MaterialEditorProps {
  jobsites: JobsiteDTO[];
  material: BillableMaterial;
  onChange: (material: BillableMaterial) => void;
  onSave: () => void;
  onDelete: (id: string) => void;
  isDirty: boolean;
}

export default function MaterialEditor({
  jobsites,
  material,
  onChange,
  onSave,
  onDelete,
  isDirty,
}: MaterialEditorProps) {
  const { t } = useTranslation();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleDelete = () => {
    onDelete(material.id);
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
  };

  const updateField = <K extends keyof BillableMaterial>(field: K, value: BillableMaterial[K]) => {
    onChange({
      ...material,
      [field]: value,
    });
  };

  const handleSelectJobsite = (jobsites: JobsiteDTO[]) => {
    const jobsiteIds = jobsites.map((j) => j.id);
    updateField('jobsiteIds', jobsiteIds);
  };

  const handleAddConditionSet = () => {
    updateField('conditionSets', [
      ...(material.conditionSets ?? []),
      {
        id: new Date().toISOString(),
        conditions: [],
      },
    ]);
  };

  const handleDeleteConditionSet = (conditionSetId: string) => {
    updateField(
      'conditionSets',
      (material.conditionSets ?? []).filter((cs) => cs.id !== conditionSetId)
    );
  };

  const handleUpdateConditionSet = (updatedRule: ConditionSet) => {
    updateField(
      'conditionSets',
      (material.conditionSets ?? []).map((cs) => (cs.id === updatedRule.id ? updatedRule : cs))
    );
  };

  const availableJobsites = useMemo(() => jobsites.filter((jobsite) => jobsite.type === 'truck'), [jobsites]);

  return (
    <Paper
      elevation={8}
      sx={{
        flex: 3,
        borderRadius: '10px',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        padding: '1em 1em 0em 1em',
        gap: '1em',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
        <Box sx={{ display: 'flex', gap: '1em', alignItems: 'center' }}>
          <TextField
            label={t('billableMaterial.articleNumber')}
            variant='outlined'
            value={material.articleNumber || ''}
            onChange={(e) => updateField('articleNumber', e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            label={t('billableMaterial.name')}
            variant='outlined'
            value={material.name || ''}
            onChange={(e) => updateField('name', e.target.value)}
            sx={{ flex: 3 }}
          />
          <IconButton onClick={() => setIsDeleteDialogOpen(true)} sx={{ height: 'max-content' }}>
            <Delete />
          </IconButton>
        </Box>
        <Box>
          <Autocomplete
            multiple
            onChange={(_, newValue) => handleSelectJobsite(newValue)}
            options={availableJobsites}
            getOptionLabel={(option) => option.name}
            value={availableJobsites.filter((jobsite) => material.jobsiteIds?.includes(jobsite.id))}
            renderInput={(params) => (
              <TextField {...params} variant='outlined' label={t('billableMaterial.jobsites')} />
            )}
            sx={{ marginBottom: 1 }}
          />
          <Typography variant='body2' color='text.secondary'>
            {t('billableMaterial.jobsiteInfo')}
          </Typography>
        </Box>
        <ConditionSetSummary conditionSets={material.conditionSets} />
        <Box>
          <Typography variant='h6'>{t('billableMaterial.rules')}</Typography>
          <Typography variant='body2'>{t('billableMaterial.rulesInfo')}</Typography>
          {material.conditionSets?.length === 0 ? (
            <Typography variant='body1'>{t('billableMaterial.noRulesConfigured')}</Typography>
          ) : (
            material.conditionSets?.map((conditionSet, i) => (
              <MaterialConditionSet
                key={conditionSet.id}
                index={i}
                conditionSet={conditionSet}
                onDelete={handleDeleteConditionSet}
                onUpdate={handleUpdateConditionSet}
              />
            ))
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          position: 'sticky',
          bottom: 0,
          background: '#fff',
          padding: '1em 0',
          borderTop: '1px solid #eeeeee',
        }}
      >
        <Button onClick={handleAddConditionSet} variant='outlined'>
          {t('billableMaterial.addRule')}
        </Button>
        <Button onClick={onSave} variant='contained' disabled={isDirty}>
          {t('common.save')}
        </Button>
      </Box>
      <DeleteMaterialDialog
        isOpen={isDeleteDialogOpen}
        onDelete={handleDelete}
        onCancel={handleDeleteCancel}
        materialName={material.name}
      />
    </Paper>
  );
}
